import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, computed, inject, signal } from '@angular/core';
import { ApproachingBusDto } from '@buspro/web/apis';
import { BusLocationMapComponent } from '../bus-location-map/bus-location-map.component';

@Component({
  selector: 'buspro-admin-approaching-buses-widget',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DatePipe, BusLocationMapComponent],
  template: `
    <div class="card bg-base-100 shadow-md">
      <div class="card-body">
        <div class="flex justify-between items-center mb-4">
          <h2 class="card-title">Approaching Buses</h2>
          <span class="text-sm text-gray-500">Updated {{ lastUpdate() | date:'short' }}</span>
        </div>
        
        <!-- Map View -->
        @if (showMap && busesSignal().length > 0) {
          <div class="mb-6">
            <buspro-admin-bus-location-map
              [buses]="busesSignal()"
              [locationCenter]="locationCenterSignal()"
              [zoom]="13"
              (bookSeat)="bookSeat.emit($event)"
              class="h-96 rounded-lg overflow-hidden"
            ></buspro-admin-bus-location-map>
          </div>
        }
        
        <div class="space-y-4">
          @if (isLoadingSignal()) {
            <div class="flex justify-center py-8">
              <span class="loading loading-spinner loading-lg"></span>
            </div>
          } @else if (hasErrorSignal()) {
            <div class="alert alert-error">
              <span>Error loading approaching buses</span>
              <button class="btn btn-sm btn-ghost" (click)="retry.emit()">Retry</button>
            </div>
          } @else {
            @for (bus of busesSignal(); track bus.operationId) {
              <div
                class="card bg-base-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                (click)="selectBus(bus)"
              >
                <div class="card-body p-4">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <h3 class="font-semibold text-lg">{{ bus.busAssetName }}</h3>
                      <p class="text-sm text-gray-600">{{ bus.routeName }}</p>
                      <p class="text-xs text-gray-500 mt-1">
                        {{ bus.fromTerminal }} → {{ bus.toTerminal }}
                      </p>
                    </div>
                    <div class="text-right ml-4">
                      <div class="text-2xl font-bold text-primary">
                        {{ bus.estimatedArrivalMinutes }} min
                      </div>
                      <div class="text-sm text-gray-600">
                        {{ bus.availableSeats }}/{{ bus.totalSeats }} seats
                      </div>
                    </div>
                  </div>

                  <!-- Destinations -->
                  @if (bus.possibleDestinations && bus.possibleDestinations.length > 0) {
                    <div class="mt-3 flex flex-wrap gap-2">
                      @for (dest of bus.possibleDestinations; track dest.terminalId) {
                        <span class="badge badge-outline">
                          {{ dest.terminalName }} - {{ dest.fare | currency:'UGX':'symbol':'1.0-0' }}
                        </span>
                      }
                    </div>
                  }

                  <!-- Action Button -->
                  <button
                    class="btn btn-primary btn-sm mt-3 w-full"
                    (click)="bookSeat.emit(bus); $event.stopPropagation()"
                    [disabled]="bus.availableSeats === 0"
                  >
                    Book Seat
                  </button>
                </div>
              </div>
            } @empty {
              <div class="text-center py-8 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-16 h-16 mx-auto mb-4 text-gray-300"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
                  />
                </svg>
                <p class="mt-2">No buses approaching at the moment</p>
              </div>
            }
          }
        </div>
      </div>
    </div>
  `,
})
export class ApproachingBusesWidgetComponent implements OnChanges {
  @Input() buses: ApproachingBusDto[] = [];
  @Input() isLoading = false;
  @Input() hasError = false;
  @Input() showMap = false;
  @Input() locationCenter: google.maps.LatLngLiteral | null = null;
  @Output() bookSeat = new EventEmitter<ApproachingBusDto>();
  @Output() retry = new EventEmitter<void>();

  busesSignal = signal<ApproachingBusDto[]>([]);
  isLoadingSignal = signal(false);
  hasErrorSignal = signal(false);
  lastUpdate = signal(new Date());
  locationCenterSignal = signal<google.maps.LatLngLiteral | null>(null);

  ngOnChanges(): void {
    this.busesSignal.set(this.buses);
    this.isLoadingSignal.set(this.isLoading);
    this.hasErrorSignal.set(this.hasError);
    this.locationCenterSignal.set(this.locationCenter);
  }

  selectBus(bus: ApproachingBusDto): void {
    // Emit event for parent to handle selection (e.g., show details, navigate, etc.)
    this.bookSeat.emit(bus);
  }
}
